import {
  hasCompletePersonalInformations,
  PatientInformationSection,
  PatientProfileWithConfiguration,
} from "../patient-profile/PatientProfile";
import * as React from "react";
import { useState } from "react";
import { getAvailableSections, PatientInformationModal } from "./PatientInformationModals";
import { Pie, PieChart } from "recharts";
import Country from "../commons/model/address/Country";

function computeProfileCompletion(
  patientProfileWithConfiguration: PatientProfileWithConfiguration
) {
  const availableSections = getAvailableSections(patientProfileWithConfiguration.configuration);
  const completedSections = availableSections.filter((section) =>
    isProfileSectionComplete(patientProfileWithConfiguration, section)
  );
  return Math.round((100 * completedSections.length) / availableSections.length);
}

function isProfileSectionComplete(
  patientProfileWithConfiguration: PatientProfileWithConfiguration,
  section: PatientInformationSection
): boolean {
  switch (section) {
    case PatientInformationSection.PERSONAL_INFO:
      return hasCompletePersonalInformations(patientProfileWithConfiguration);
    case PatientInformationSection.DENTAL_INFO:
      return !!patientProfileWithConfiguration.patientProfile.dentalInformation;
    case PatientInformationSection.HEALTH_HISTORY_INFO:
      return !!patientProfileWithConfiguration.patientProfile.healthHistoryInformation;
    case PatientInformationSection.INSURANCE_INFO:
      return !!patientProfileWithConfiguration.patientProfile.primaryInsuranceInformation;
    case PatientInformationSection.AUTHORIZATION_INFO:
      return !!patientProfileWithConfiguration.patientProfile.authorizationInformation;
    case PatientInformationSection.HOUSEHOLD_INFO:
      return !!patientProfileWithConfiguration.patientProfile.householdInformation;
    default:
      console.error(`invalid profile section: ${section}`);
  }
}

function PercentageCircle({ percentage }: { percentage: number }) {
  return (
    <div className="percentage-circle">
      <PieChart width={125} height={125}>
        <Pie
          animationDuration={500}
          dataKey="value"
          data={[
            { value: percentage, className: "completed" },
            { value: 100 - percentage, className: "remaining" },
          ]}
          startAngle={90}
          endAngle={-270}
          innerRadius="70%"
          outerRadius="100%"
          stroke={"none"}
        />
      </PieChart>
      <div className="percentage">
        <span className="value">{percentage}</span>
        <span className="percent">%</span>
      </div>
    </div>
  );
}

function getSectionI18nKey(section: PatientInformationSection): string {
  switch (section) {
    case PatientInformationSection.PERSONAL_INFO:
      return "Page.Portal.PersonalInformation.Title";
    case PatientInformationSection.HOUSEHOLD_INFO:
      return "Page.Portal.HouseholdInformation.Title";
    case PatientInformationSection.INSURANCE_INFO:
      return "Page.Portal.InsuranceInformation.Title";
    case PatientInformationSection.DENTAL_INFO:
      return "Page.Portal.DentalInformation.Title";
    case PatientInformationSection.HEALTH_HISTORY_INFO:
      return "Page.Portal.HealthHistoryInformation.Title";
    case PatientInformationSection.AUTHORIZATION_INFO:
      return "Page.Portal.AuthorizationInformation.Title";
  }
}

function ProfileSectionButton({
  section,
  completed,
  onClick,
}: {
  section: PatientInformationSection;
  completed: boolean;
  onClick: () => void;
}) {
  return (
    <div onClick={onClick} className="profile-section-button">
      <span>
        {i18n[getSectionI18nKey(section)]}
        {completed && <i className="ic_check_circled" />}
      </span>
      <i className="ic_chevron_right" />
    </div>
  );
}

export function ProfileCompletion({
  patientProfileWithConfiguration,
  availableCountries,
  forMyself,
}: {
  patientProfileWithConfiguration: PatientProfileWithConfiguration;
  availableCountries: Country[];
  forMyself: boolean;
}) {
  const [openedForm, setOpenedForm] = useState<PatientInformationSection>();
  const profileCompletionPercentage = computeProfileCompletion(patientProfileWithConfiguration);
  return (
    <>
      <div id="profile-completion" className="sub-panel">
        <div className="panel-title">{i18n["Page.Portal.ProfileCompletion.Title"]}</div>
        <PercentageCircle percentage={profileCompletionPercentage} />
        {profileCompletionPercentage !== 100 && (
          <>
            <div className="instructions">{i18n["Page.Portal.ProfileCompletion.Instructions"]}</div>
            <div className={"buttons"}>
              {getAvailableSections(patientProfileWithConfiguration.configuration).map(
                (section) => (
                  <ProfileSectionButton
                    key={section}
                    section={section}
                    completed={isProfileSectionComplete(patientProfileWithConfiguration, section)}
                    onClick={() => {
                      setOpenedForm(section);
                    }}
                  />
                )
              )}
            </div>
          </>
        )}
      </div>
      <PatientInformationModal
        patientProfileWithConfiguration={patientProfileWithConfiguration}
        section={openedForm}
        availableCountries={availableCountries}
        closeModal={() => {
          setOpenedForm(null);
        }}
        forMyself={forMyself}
      />
    </>
  );
}
